(() => {
  'use strict';

  const ROOT = '[data-zg-reader]';
  const normalizedText = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const slug = (value) => Array.from(String(value || '').normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, ''))
    .slice(0, 48).join('').replace(/-+$/g, '');

  function createReader(doc, win) {
    const instances = new Map();
    let started = false;
    const rootsWithin = (scope = doc) => [
      ...(scope.matches?.(ROOT) ? [scope] : []),
      ...Array.from(scope.querySelectorAll?.(ROOT) || [])
    ];
    const visibleHeading = (heading) => {
      if (heading.closest('[hidden], [aria-hidden="true"]')) return false;
      for (let element = heading; element; element = element.parentElement) {
        const style = win.getComputedStyle?.(element);
        if (style?.display === 'none' || style?.visibility === 'hidden' || style?.visibility === 'collapse') return false;
        if (element.matches(ROOT)) break;
      }
      return true;
    };
    const restoreAttribute = (element, name, value) => {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    };
    const currentFragment = () => {
      try { return decodeURIComponent((win.location?.hash || '').slice(1)); }
      catch (_) { return null; }
    };
    const queueFocus = (state, heading, initial = false, sourceEvent = null) => {
      if (state.frame !== null) win.cancelAnimationFrame(state.frame);
      state.frame = win.requestAnimationFrame(() => {
        state.frame = null;
        if (instances.get(state.root) !== state || !state.prose.contains(heading)) return;
        // A later bubbling listener may cancel navigation, or a newer native
        // navigation may win before this frame. Never focus a stale destination.
        if (sourceEvent?.defaultPrevented || currentFragment() !== heading.id) return;
        if (!heading.hasAttribute('tabindex')) {
          state.focusAttributes.set(heading, null);
          heading.setAttribute('tabindex', '-1');
        }
        heading.focus({ preventScroll: true });
        const reduced = win.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        // Native link navigation owns the URL/history. "instant" also overrides a
        // site's global smooth-scroll CSS when reduced motion or a reload applies.
        heading.scrollIntoView({ block: 'start', behavior: initial || reduced ? 'instant' : 'smooth' });
      });
    };

    function initRoot(root) {
      if (instances.has(root)) return instances.get(root);
      const prose = root.querySelector('[data-zg-prose]');
      const toc = root.querySelector('[data-zg-toc]');
      if (!prose || !toc) return null;
      if (prose.closest(ROOT) !== root || toc.closest(ROOT) !== root) return null;
      toc.hidden = true;
      const articleTitle = normalizedText(root.querySelector('h1')?.textContent).toLowerCase();
      const headings = Array.from(prose.querySelectorAll('h2, h3, h4'))
        .map((heading, sourceIndex) => ({ heading, label: normalizedText(heading.textContent), sourceIndex }))
        // Some legacy articles use H3 for the repeated title and H4 for prose.
        // Filter their outline entries only; never change the original content.
        .filter(({ heading, label }) => label
          && label.toLowerCase() !== articleTitle
          && Array.from(label).length <= 180
          && visibleHeading(heading));
      if (headings.length < 2) return null;

      // Keep the document's first target for each existing ID. Only later
      // conflicting headings receive a new ID; old bookmarks retain their target.
      const firstOwner = new Map();
      const reserved = new Set();
      for (const element of doc.querySelectorAll('[id]')) {
        if (!element.id) continue;
        reserved.add(element.id);
        if (!firstOwner.has(element.id)) firstOwner.set(element.id, element);
      }
      const rootIndex = Math.max(0, rootsWithin(doc).indexOf(root)) + 1;
      const rootKey = slug(root.id) || 'reader-' + rootIndex;
      const list = doc.createElement('ol');
      list.className = 'zg-toc__list';
      list.setAttribute('data-zg-toc-list', '');
      const state = { root, prose, toc, list, headings, frame: null, focusAttributes: new Map(), margins: new Map() };
      const targets = new Map();
      headings.forEach(({ heading, label, sourceIndex }) => {
        if (!heading.id || (firstOwner.has(heading.id) && firstOwner.get(heading.id) !== heading)) {
          const base = 'zg-' + rootKey + '-' + (sourceIndex + 1) + '-' + (slug(label) || 'section');
          let id = base;
          let suffix = 2;
          while (reserved.has(id)) id = base + '-' + suffix++;
          heading.id = id;
          reserved.add(id);
          firstOwner.set(id, heading);
        }
        state.margins.set(heading, {
          value: heading.style.getPropertyValue('scroll-margin-top'),
          priority: heading.style.getPropertyPriority('scroll-margin-top')
        });
        heading.style.setProperty('scroll-margin-top', 'var(--zg-reader-scroll-offset, 110px)');
        const item = doc.createElement('li');
        item.className = 'zg-toc__item';
        item.setAttribute('data-zg-toc-level', heading.tagName.toLowerCase());
        const link = doc.createElement('a');
        link.setAttribute('data-zg-toc-link', '');
        link.setAttribute('href', '#' + encodeURIComponent(heading.id));
        link.textContent = label;
        targets.set(link, heading);
        item.appendChild(link);
        list.appendChild(item);
      });
      state.onClick = (event) => {
        if (event.defaultPrevented || (event.button !== undefined && event.button !== 0)
          || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const element = event.target?.closest ? event.target : event.target?.parentElement;
        const link = element?.closest('[data-zg-toc-link]');
        const heading = targets.get(link);
        if (heading) queueFocus(state, heading, false, event);
      };
      toc.appendChild(list);
      toc.addEventListener('click', state.onClick);
      toc.hidden = false;
      instances.set(root, state);
      // A generated ID is available after hydration on a bookmarked page.
      const fragment = currentFragment();
      const bookmarked = headings.find(({ heading }) => heading.id === fragment);
      if (bookmarked) queueFocus(state, bookmarked.heading, true);
      return state;
    }

    function destroy(root) {
      const state = instances.get(root);
      if (!state) return;
      if (state.frame !== null) win.cancelAnimationFrame(state.frame);
      state.toc.removeEventListener('click', state.onClick);
      state.list.remove();
      state.toc.hidden = true;
      for (const [heading, oldValue] of state.focusAttributes) {
        if (heading.getAttribute('tabindex') === '-1') restoreAttribute(heading, 'tabindex', oldValue);
      }
      for (const [heading, previous] of state.margins) {
        if (heading.style.getPropertyValue('scroll-margin-top') !== 'var(--zg-reader-scroll-offset, 110px)') continue;
        if (previous.value) heading.style.setProperty('scroll-margin-top', previous.value, previous.priority);
        else heading.style.removeProperty('scroll-margin-top');
      }
      // IDs remain stable for links already copied or in the browser's history.
      instances.delete(root);
    }
    const init = (scope = doc) => rootsWithin(scope).map(initRoot);
    const onReady = () => init(doc);
    const onLoad = (event) => {
      rootsWithin(event.target).forEach(destroy);
      init(event.target);
    };
    const onUnload = (event) => rootsWithin(event.target).forEach(destroy);
    function start() {
      if (started) return;
      started = true;
      doc.addEventListener('shopify:section:load', onLoad);
      doc.addEventListener('shopify:section:unload', onUnload);
      if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', onReady, { once: true });
      else init(doc);
    }
    function stop() {
      doc.removeEventListener('DOMContentLoaded', onReady);
      doc.removeEventListener('shopify:section:load', onLoad);
      doc.removeEventListener('shopify:section:unload', onUnload);
      Array.from(instances.keys()).forEach(destroy);
      started = false;
    }
    return { init, destroy, start, stop };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createReader };
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__ziqingReaderController) {
    window.__ziqingReaderController.init(document);
    return;
  }
  const controller = createReader(document, window);
  window.__ziqingReaderController = controller;
  controller.start();
})();
